import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';

import {ActionsDispatcher} from './actions_dispatcher';
import {ActionsHandler} from './actions_handler';
import {Graph, Link, Node} from './graph';
import {LabState} from './lab_state';
import {PipeContext, PipedGraphOverlay} from './pipes';

export async function settle(
  graph: Graph,
  handler: ActionsHandler,
  sandbox: boolean = false,
): Promise<void> {
  const roots = [];
  for (const node of graph.nodes.values()) {
    // Find roots
    let root = true;
    for (const input of node.inputs.values()) {
      if (input.links.size > 0) {
        root = false;
        break;
      }
    }
    if (root) {
      roots.push(node);
    }
  }

  const dispatcher = new ActionsDispatcher(
    handler,
    // This ends up being the tempo of the run. If every task is instant then this time slows
    // everything down, but if things take time then it probably averages out
    /* pollMs= */ 1,
  );
  const overlay = new PipedGraphOverlay(dispatcher, graph);
  graph.overlay = overlay;

  const sorted = topological(roots);
  for (const node of sorted) {
    node.pipe?.({graph, node, overlay});
  }

  const context: PipeContext = {
    changes: overlay,
    dispatcher,
    lab: new LabState(sandbox),
  };
  await context.lab.initialize(context.dispatcher);

  for (const source of overlay.initialSources()) {
    // Errors in these promises do get caught in the dispatcher, so don't let them be considered
    // uncaught here.
    source.act(context).catch(() => {});
  }
  context.dispatcher.initialized();
  await context.dispatcher.waitForFinish();
  await overlay.commit();
}

function topological(roots: Iterable<Node>): Node[] {
  const sorted = [];
  const seen = new Set<Link>();
  const visited = new Set<Node>();
  const scratch = [...roots];
  while (scratch.length > 0) {
    const node = checkExists(scratch.shift());
    if (visited.has(node)) {
      continue;
    }
    sorted.push(node);
    visited.add(node);

    for (const output of node.outputs.values()) {
      for (const link of output.links.values()) {
        seen.add(link);

        let empty = true;
        for (const input of link.input.node.inputs.values()) {
          for (const other of input.links.values()) {
            if (!seen.has(other)) {
              empty = false;
              break;
            }
          }
        }
        if (empty) {
          scratch.push(link.input.node);
        }
      }
    }
  }
  return sorted;
}
